import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Lock } from 'lucide-react';
import { CHAIN } from '../../lib/u99';
import { Reveal, Eyebrow } from '../../components/Reveal';
import { PageHead, Chip } from '../../components/u99/PageHead';
import { Label } from '../../components/u99/Label';

export default function Tools() {
  useEffect(() => { document.title = 'Psychology Tools — United 99 Unleashed'; }, []);
  return (
    <main className="max-w-7xl mx-auto px-5 lg:px-10" data-testid="tools-page">
      <PageHead eyebrow="Psychology Tools · Guidance, not diagnosis" accent="#22D3EE" title={<>Instruments for <span className="text-cyan-300">seeing</span></>} lede="Simple, interactive, and deliberately modest. Each tool helps you notice a sequence you are already living. None of them tell you what you are." />

      <section className="py-8 lg:py-12 border-t border-white/[0.07]">
        <Reveal>
          <Link to="/u99/tools/trace" className="group block rounded-3xl border border-cyan-400/25 hover:border-cyan-300/60 p-7 lg:p-12 transition-colors duration-500 relative overflow-hidden" data-testid="tool-card-trace">
            <span className="absolute -right-6 -top-10 font-celestial text-[10rem] leading-none text-white/[0.025] select-none">01</span>
            <div className="flex flex-wrap items-center gap-4">
              <span className="font-mono-precision text-[10px] uppercase tracking-[0.3em] text-cyan-300">Tool 01 · Live</span>
              <Label id="psychological-guidance" size="xs" />
            </div>
            <h2 className="mt-6 font-celestial text-3xl sm:text-4xl text-slate-100 leading-tight group-hover:text-cyan-100 transition-colors">Trace a Reaction</h2>
            <p className="mt-5 text-sm sm:text-base text-slate-400 leading-relaxed max-w-2xl">Take one present reaction and walk it backward — action, thought, emotion, knowing, memory, experience — until the pattern it belongs to has a name. Seven questions. Four minutes. Nothing leaves your device.</p>
            <div className="mt-8 flex flex-wrap items-center gap-1.5" data-testid="tool-chain">
              {CHAIN.map((c, i) => (
                <span key={c} className="flex items-center gap-1.5">
                  {i > 0 && <span className="text-cyan-400/50 font-mono-precision text-xs">→</span>}
                  <span className="font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-300">{c}</span>
                </span>
              ))}
            </div>
            <span className="mt-10 inline-flex items-center gap-3 font-mono-precision text-[11px] uppercase tracking-[0.25em] text-cyan-300">Open the tool <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></span>
          </Link>
        </Reveal>
      </section>

      <section className="py-8 lg:py-12 border-t border-white/[0.07]">
        <Reveal><Eyebrow accent="#FBBF24">Held for later</Eyebrow></Reveal>
        <div className="grid sm:grid-cols-2 gap-4">
          {[['Polarity Reading', 'Placeholder — deeper Polarity work is parked until the foundation is live.'], ['Whisp', 'Placeholder — reserved.']].map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.07}>
              <div className="rounded-2xl border border-dashed border-amber-400/25 p-6 opacity-80" data-testid={`tool-future-${t.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between"><Chip>Future</Chip><Lock size={14} className="text-amber-400/50" /></div>
                <h3 className="mt-5 font-celestial text-xl text-slate-200">{t}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
